import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getMessageHistory, sendMessageText } from "../../api";
import { onResponseModeToggled } from '../common/thunks';

export const onChatWindowInitiated = createAsyncThunk(
  "chat/onChatWindowInitiated",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.auth.token;
    const sessionId = state.auth.sessionId;
    
    // If token is null or empty, reject early to avoid 401 error
    if (!token) {
      return rejectWithValue({ status: 401, message: 'No authentication token' });
    }
    
    try {
      const messageHistory = await getMessageHistory(token, sessionId, null);
      return messageHistory;
    } catch (error) {
      // Return the error so it can be handled in the rejected case
      // Axios errors have error.response.status, regular errors might have error.status
      const status = error.response?.status || error.status || 500;
      return rejectWithValue({ status, message: error.message || 'Request failed' });
    }
  }
);

export const onNewTextMessageSubmitted = createAsyncThunk(
  "chat/onNewTextMessageSubmitted",
  async (message, { getState }) => {
    let messageTrimmed = message.trim();
    if (messageTrimmed.length < 1) {
      return [];
    }
    const state = getState();
    const token = state.auth.token;
    const sessionId = state.auth.sessionId;
    const messages = await sendMessageText(token, sessionId, messageTrimmed);
    return messages;
  }
);

export const onRequestMoreConversationHistory = createAsyncThunk(
  "chat/onRequestMoreConversationHistory",
  async (_, { getState }) => {
    const state = getState();
    const token = state.auth.token;
    const sessionId = state.auth.sessionId;
    const messages = state.chat.messages;
    const oldestMessageId = messages[0]?.id ?? null;
    const messageHistory = await getMessageHistory(token, sessionId, oldestMessageId);
    return messageHistory;
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    isInitiated: false,
    messages: [],
    isFetching: false,
    isFetchingMoreHistory: false,
    hasUnloadedHistory: true,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(onChatWindowInitiated.pending, (state) => {
      state.isFetching = true;
    });

    builder.addCase(onChatWindowInitiated.fulfilled, (state, { payload }) => {
      state.isInitiated = true;
      state.messages = payload.map(({ _id, content, source, track_id }) => ({
        id: _id,
        content,
        source,
        trackId: track_id,
      }));
      state.isFetching = false;
    });

    builder.addCase(onChatWindowInitiated.rejected, (state, { payload, error }) => {
      state.isFetching = false;
      // If it's a 401 error, still mark as initiated to prevent retry loops
      // but leave messages empty. The user will need to authenticate.
      const status = payload?.status || error?.response?.status || error?.status;
      if (status === 401) {
        state.isInitiated = true;
        state.messages = [];
      }
    });

    builder.addCase(
      onNewTextMessageSubmitted.pending,
      (state, { meta: { arg } }) => {
        state.messages.push({
          id: "TEMP_ID",
          source: "user",
          content: arg,
        });
        state.isFetching = true;
      }
    );
    builder.addCase(onNewTextMessageSubmitted.fulfilled, (state, action) => {
      state.messages.pop();
      action.payload.forEach(({ _id, content, source, track_id }) => {
        state.messages.push({
          id: _id,
          content,
          source,
          trackId: track_id,
        });
      });

      state.isFetching = false;
    });

    builder.addCase(onNewTextMessageSubmitted.rejected, (state, { meta, error }) => {
      // Remove the temporary message that was added in pending
      state.messages.pop();
      state.isFetching = false;
      // You could optionally add error state here if needed
    });
    builder.addCase(onRequestMoreConversationHistory.pending, (state) => {
      state.isFetchingMoreHistory = true;
    });
    builder.addCase(onRequestMoreConversationHistory.fulfilled, (state, { payload }) => {
      if (!payload.length) {
        state.hasUnloadedHistory = false;
      }

      state.messages = [...payload.map(({ _id, content, source, track_id }) => ({
        id: _id,
        content,
        source,
        trackId: track_id,
      })), ...state.messages];
      state.isFetchingMoreHistory = false;
    });

    builder.addCase(onRequestMoreConversationHistory.rejected, (state) => {
      state.isFetchingMoreHistory = false;
      // On error, assume no more history is available to prevent retry loops
      state.hasUnloadedHistory = false;
    });

    builder.addCase(onResponseModeToggled.pending, (state, { payload }) => {
      state.isFetching = true;
    });

    builder.addCase(onResponseModeToggled.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      if (!payload) return;

      state.messages = payload.map(({ _id, content, source, track_id }) => ({
        id: _id,
        content,
        source,
        trackId: track_id,
      }));

    });
  },
});

export default chatSlice.reducer;
